// Zustand Store for Chat

import { create } from 'zustand'
import type { ChatStore, ChatRoomWithDetails, ChatMessage } from '@/types/chat'

// 전역 unread count 상태 (Header와 BottomNav에서 공유)
interface GlobalUnreadState {
  totalUnread: number
  lastFetchTime: number
  isFetching: boolean
  setTotalUnread: (count: number) => void
  fetchUnreadCount: (force?: boolean) => Promise<void>
}

const MIN_FETCH_INTERVAL = 30000 // 30초

export const useGlobalUnreadStore = create<GlobalUnreadState>((set, get) => ({
  totalUnread: 0,
  lastFetchTime: 0,
  isFetching: false,

  setTotalUnread: (count) => set({ totalUnread: count }),

  fetchUnreadCount: async (force = false) => {
    const { lastFetchTime, isFetching } = get()
    const now = Date.now()

    // 이미 fetching 중이거나, 최소 간격 미충족 시 스킵
    if (isFetching) return
    if (!force && now - lastFetchTime < MIN_FETCH_INTERVAL) return

    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    if (!token) {
      set({ totalUnread: 0 })
      return
    }

    set({ isFetching: true, lastFetchTime: now })

    try {
      const response = await fetch('/api/chat/unread-count', {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.status === 429) {
        // Rate limit - 1분 대기
        set({ lastFetchTime: now + 60000, isFetching: false })
        return
      }

      if (response.ok) {
        const result = await response.json()
        set({
          totalUnread: result.data?.total_unread || result.unread_count || 0,
          isFetching: false
        })
      } else {
        set({ isFetching: false })
      }
    } catch (error) {
      set({ isFetching: false })
    }
  }
}))

export const useChatStore = create<ChatStore>((set, get) => ({
  // 상태
  currentRoomId: null,
  rooms: [],
  messages: {},
  unreadCounts: {},
  typingUsers: {},
  isLoading: false,
  error: null,

  // 액션

  setCurrentRoom: (roomId) => {
    set({ currentRoomId: roomId })
  },

  setRooms: (rooms) => {
    set({ rooms })
  },

  addRoom: (room) => {
    const { rooms } = get()
    const exists = rooms.some((r) => r.id === room.id)
    if (!exists) {
      set({ rooms: [room, ...rooms] })
    }
  },

  updateRoom: (roomId, updates) => {
    const { rooms } = get()
    set({
      rooms: rooms.map((room) =>
        room.id === roomId ? { ...room, ...updates } : room
      )
    })
  },

  addMessage: (roomId, message) => {
    const { messages, rooms } = get()
    const roomMessages = messages[roomId] || []

    // 중복 메시지 체크
    const exists = roomMessages.some((m) => m.id === message.id)
    if (!exists) {
      // 메시지 추가와 채팅방 업데이트를 단일 setState로 처리
      set({
        messages: {
          ...messages,
          [roomId]: [...roomMessages, message]
        },
        rooms: rooms.map((room) =>
          room.id === roomId
            ? {
                ...room,
                last_message_at: message.created_at,
                last_message: {
                  id: message.id,
                  sender_id: message.sender_id,
                  content: message.content,
                  message_type: message.message_type,
                  created_at: message.created_at
                }
              }
            : room
        )
      })
    }
  },

  setMessages: (roomId, messages) => {
    set((state) => ({
      messages: {
        ...state.messages,
        [roomId]: messages
      }
    }))
  },

  updateMessage: (roomId, messageId, updates) => {
    const { messages } = get()
    const roomMessages = messages[roomId] || []

    set({
      messages: {
        ...messages,
        [roomId]: roomMessages.map((msg) =>
          msg.id === messageId ? { ...msg, ...updates } : msg
        )
      }
    })
  },

  deleteMessage: (roomId, messageId) => {
    const { messages } = get()
    const roomMessages = messages[roomId] || []

    set({
      messages: {
        ...messages,
        [roomId]: roomMessages.map((msg) =>
          msg.id === messageId
            ? { ...msg, is_deleted: true, content: '삭제된 메시지입니다' }
            : msg
        )
      }
    })
  },

  setUnreadCount: (roomId, count) => {
    const { rooms } = get()
    // 읽지 않은 수와 채팅방 업데이트를 단일 setState로 처리
    set({
      unreadCounts: {
        ...get().unreadCounts,
        [roomId]: count
      },
      rooms: rooms.map((room) =>
        room.id === roomId ? { ...room, unread_count: count } : room
      )
    })
  },

  decrementUnreadCount: (roomId, amount = 1) => {
    const { unreadCounts, rooms } = get()
    const currentCount = unreadCounts[roomId] || 0
    const newCount = Math.max(0, currentCount - amount)

    // 읽지 않은 수 감소와 채팅방 업데이트를 단일 setState로 처리
    set({
      unreadCounts: {
        ...unreadCounts,
        [roomId]: newCount
      },
      rooms: rooms.map((room) =>
        room.id === roomId ? { ...room, unread_count: newCount } : room
      )
    })
  },

  clearUnreadCount: (roomId) => {
    const { rooms } = get()
    // 읽지 않은 수 초기화와 채팅방 업데이트를 단일 setState로 처리
    set({
      unreadCounts: {
        ...get().unreadCounts,
        [roomId]: 0
      },
      rooms: rooms.map((room) =>
        room.id === roomId ? { ...room, unread_count: 0 } : room
      )
    })
  },

  setTypingUser: (roomId, userId, isTyping) => {
    const { typingUsers } = get()
    const currentTyping = typingUsers[roomId] || []

    if (isTyping) {
      // 타이핑 사용자 추가
      if (!currentTyping.includes(userId)) {
        set({
          typingUsers: {
            ...typingUsers,
            [roomId]: [...currentTyping, userId]
          }
        })
      }
    } else {
      // 타이핑 사용자 제거
      set({
        typingUsers: {
          ...typingUsers,
          [roomId]: currentTyping.filter((id) => id !== userId)
        }
      })
    }
  },

  setLoading: (isLoading) => {
    set({ isLoading })
  },

  setError: (error) => {
    set({ error })
  },

  reset: () => {
    set({
      currentRoomId: null,
      rooms: [],
      messages: {},
      unreadCounts: {},
      typingUsers: {},
      isLoading: false,
      error: null
    })
  }
}))

// 헬퍼 함수들

/**
 * 특정 채팅방의 메시지 가져오기
 */
export const getRoomMessages = (roomId: string): ChatMessage[] => {
  const store = useChatStore.getState()
  return store.messages[roomId] || []
}

/**
 * 특정 채팅방의 읽지 않은 메시지 수 가져오기
 */
export const getUnreadCount = (roomId: string): number => {
  const store = useChatStore.getState()
  return store.unreadCounts[roomId] || 0
}

/**
 * 전체 읽지 않은 메시지 수 가져오기
 */
export const getTotalUnreadCount = (): number => {
  const store = useChatStore.getState()
  return Object.values(store.unreadCounts).reduce((sum, count) => sum + count, 0)
}

/**
 * 특정 채팅방에서 타이핑 중인 사용자 가져오기
 */
export const getTypingUsers = (roomId: string): string[] => {
  const store = useChatStore.getState()
  return store.typingUsers[roomId] || []
}

/**
 * 특정 채팅방 정보 가져오기
 */
export const getRoom = (roomId: string): ChatRoomWithDetails | undefined => {
  const store = useChatStore.getState()
  return store.rooms.find((r) => r.id === roomId)
}
